import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { TrialTerms, useTrialEligibility } from "@/components/billing/TrialTerms";
import { PrimaryButton, TextLink } from "@/components/fionova/Primitives";
import { track, trackSessionOnce } from "@/lib/analytics";
import { LegalNotice } from "@/components/fionova/LegalNotice";

export const TrialOffer = ({ onStart, checkingOut }) => {
  const [declined, setDeclined] = useState(false);
  const offer = useTrialEligibility();
  const decline = () => { setDeclined(true); track("trial_declined", { plan: offer?.key || "unknown" }); };

  return (
    <motion.section data-testid="xray-reveal-trial" onViewportEnter={() => trackSessionOnce("trial_offer_viewed", "trial_offer_viewed", { plan: offer?.key || "unknown" })}
      initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.7 }}
      className="fio-glass-strong fio-hairline relative mt-16 overflow-hidden rounded-[32px] p-7">
      <span aria-hidden className="absolute -left-16 -top-16 h-48 w-48 rounded-full bg-teal-300/30 blur-3xl" />
      <span aria-hidden className="absolute -bottom-20 -right-16 h-56 w-56 rounded-full bg-violet-300/30 blur-3xl" />
      <div className="relative">
        <p className="eyebrow text-[11px] text-teal-700">{offer?.eyebrow || "Full premium access"}</p>
        <h2 className="mt-3 text-[28px] font-bold leading-tight tracking-tight text-slate-900" data-testid="trial-headline">
          Continue your journey <span className="fio-gradient-text">with Ellie</span>
        </h2>
        <p className="mt-3 text-[15.5px] leading-relaxed text-slate-700" data-testid="trial-supporting">
          Explore your Future Self, Daily Battle Plan, Purpose Engine, and everything FioNova has to offer.
        </p>
        <div className="mt-6 rounded-3xl bg-white/80 p-6 text-center ring-1 ring-slate-200">
          <p className="text-4xl font-bold tracking-tight text-slate-900" data-testid="trial-price">
            {offer?.today || "…"} <span className="text-base font-normal text-slate-500">due today</span>
          </p>
        </div>
        <TrialTerms offer={offer} className="mt-5" />
        <LegalNotice action="subscribing" testId="trial-legal" className="mt-4" />
        <div className="mt-7">
          <PrimaryButton className="fio-cta !py-4 text-[15px] uppercase tracking-wide" data-testid="trial-cta" onClick={onStart} disabled={checkingOut || !offer}>
            {checkingOut ? "Opening secure checkout…" : offer?.cta || "Loading current pricing…"}
            {!checkingOut && <ArrowRight className="h-4 w-4" />}
          </PrimaryButton>
        </div>
        {declined ? (
          <p className="mt-4 text-center text-sm leading-relaxed text-slate-600" data-testid="trial-declined-note">
            No problem. Your results are saved to your free account — log in anytime to see them again.
          </p>
        ) : (
          <div className="mt-4 text-center">
            <TextLink data-testid="trial-decline" className="!text-sm" onClick={decline}>Not right now</TextLink>
          </div>
        )}
      </div>
    </motion.section>
  );
};
